/* eslint-disable sonarjs/cognitive-complexity */

import React, { createContext, ReactNode, useState, useMemo } from "react";
import qs from "query-string";

import { UWP_CREDENTIAL_RESOURCE, useProvisioningMachine, BaseContextType } from "./provisioningMachine";
import { MissingConfigNotification } from "./ui/MissingConfigNotification";
import { AuthStoredCredentials, doAuthRequest, defaultAuth0Config } from "./auth";
import { Auth0Config } from "./auth/doAuthRequest";
import { ProvisioningContextType, BaseConfigType, CredentialType } from "./types";
import InteractiveProvisioningForm from "./ui/InteractiveProvisioningForm";
import { AsyncStorage } from "./native";
import { RavenLogoSVG, GradiantScreen, Spinner } from "@raven/shared/components";
import { ThemeProvider, Theme, StyledEngineProvider, createTheme } from "@mui/material/styles";
import { getWhitelabelConfig } from "@raven/shared/utils";

declare module "@mui/styles/defaultTheme" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

const theme = createTheme();

export const ProvisioningContext = createContext<ProvisioningContextType>({} as ProvisioningContextType);

type WhitelabelConfig = {
  logoUrl?: string;
  logoHeight?: number;
  logoWidth?: number;
};

export interface AuthAndChildrenProps extends BaseConfigType, CredentialType {
  auth0Config: Auth0Config;
  children: ReactNode;
  onLogin?: () => void;
}

/**
 * This centers content in a visually pleasing way.
 * It's not direct center, it's placed a little above.
 * */
const CenterContent = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const whitelabelConfig = getWhitelabelConfig() as WhitelabelConfig;
  return (
    <GradiantScreen>
      <div
        style={{
          paddingTop: "20vh",
        }}
      >
        {whitelabelConfig?.logoUrl ? (
          <img
            src={whitelabelConfig.logoUrl}
            style={{
              height: (whitelabelConfig.logoHeight ?? 32) + "px",
              width: whitelabelConfig.logoWidth ? whitelabelConfig.logoWidth + "px" : "auto",
            }}
          />
        ) : (
          <RavenLogoSVG logoWidth={"25vh"} />
        )}
      </div>
      <div
        style={{
          paddingTop: "2vh",
          paddingBottom: "40vh",
        }}
      >
        {children}
      </div>
    </GradiantScreen>
  );
};

const AuthAndChildren = ({
  username,
  password,
  serial,
  auth0Config,
  onLogin,
  children,
  ...extra
}: AuthAndChildrenProps): React.JSX.Element => {
  const [state, setState] = useState<BaseContextType>({
    username,
    password,
    serial,
  });

  if (state.username && state.password) {
    return (
      <AuthStoredCredentials {...{ username: state.username, password: state.password, auth0Config }}>
        <ProvisioningContext.Provider
          value={{
            ...extra,
            serial: state.serial,
          }}
        >
          {children}
        </ProvisioningContext.Provider>
      </AuthStoredCredentials>
    );
  }

  return (
    <CenterContent>
      <InteractiveProvisioningForm
        initialValues={{
          username: state.username,
          password: state.password,
          serial: state.serial,
        }}
        onSubmit={({ username: newUsername, password: newPassword, serial: newSerial }) => {
          setState({
            username: newUsername,
            password: newPassword,
            serial: newSerial,
            // tenant: newTenant,
          });
          onLogin && onLogin();
        }}
        onValidate={(usernameCandidate: string, passwordCandidate: string) =>
          doAuthRequest(usernameCandidate, passwordCandidate, auth0Config)
        }
      />
    </CenterContent>
  );
};
export interface ProvisioningProps {
  auth0Config?: Auth0Config;
  children: ReactNode;
  onLogin?: () => void;
}

const RenderAuth = ({
  children,
  auth0Config = defaultAuth0Config,
  onLogin,
}: ProvisioningProps): React.JSX.Element => {
  const [state, send] = useProvisioningMachine();
  const { username: initUsername, password: initPassword, serial: initSerial, ...extra } = state.context;

  const { username, password, serial } = useMemo(
    () => contextFromQueryString(initUsername, initPassword, initSerial),
    [initUsername, initPassword, initSerial]
  );

  if (shouldSkipProvisioning()) {
    return (
      <ProvisioningContext.Provider value={{ username, password, serial } as any}>
        {children}
      </ProvisioningContext.Provider>
    );
  }

  const shouldRenderAuthAndChildren = () => {
    // TypeScript picks value up as always a string, but for hierarchical states
    // is an object or nested objects, so make state.value any before trying to
    // access any fields.
    if ((state.value as any)?.uwp === "success") {
      console.log("Provisioned as UWP app");
      return true;
    } else if ((state.value as any)?.legacyMunin === "success") {
      console.log("Provisioned as legacy Munin app");
      return true;
    } else if ((state.value as any)?.ios === "success") {
      console.log("Provisioned as native iOS app");
      return true;
    } else if ((state.value as any)?.pwa === "success") {
      console.log("Provisioned as PWA app");
      return true;
    }
  };

  if (shouldRenderAuthAndChildren()) {
    return (
      <AuthAndChildren
        {...{
          ...extra,
          username,
          password,
          auth0Config,
          serial,
          onLogin,
          children,
        }}
      />
    );
  } else if ((state.value as any)?.uwp === "setFile") {
    // TODO: Make it possible to fill in the config file here
    // If the config file isn't present, display a notification
    return <MissingConfigNotification onClickRetry={() => send("DONE")} />;
  } else if ((state.value as any)?.uwp === "setCredentials") {
    // If the password isn't present in the credential storage, show a form to
    // capture the password. This is where we set the credentials for the
    // username in the credential store.
    return (
      <CenterContent>
        <InteractiveProvisioningForm
          initialValues={state.context}
          onValidate={(usernameCandidate: string, passwordCandidate: string) =>
            doAuthRequest(usernameCandidate, passwordCandidate, auth0Config)
          }
          onSubmit={({ password }) => {
            // eslint-disable-next-line no-undef
            const passwordCredential = Windows.Security.Credentials.PasswordCredential(
              UWP_CREDENTIAL_RESOURCE,
              state.context.username,
              password
            );
            // eslint-disable-next-line no-undef
            const passwordVault = Windows.Security.Credentials.PasswordVault();
            passwordVault.add(passwordCredential);
            send("DONE");
            onLogin && onLogin();
          }}
        />
      </CenterContent>
    );
  } else if ((state.value as any)?.ios === "setFile") {
    return (
      <CenterContent>
        <InteractiveProvisioningForm
          initialValues={state.context}
          onSubmit={async ({ username, password, serial }) => {
            await AsyncStorage.setItem<BaseContextType>("config", { username, password, serial });
            send("DONE");
            onLogin && onLogin();
          }}
          onValidate={(usernameCandidate: string, passwordCandidate: string) =>
            doAuthRequest(usernameCandidate, passwordCandidate, auth0Config)
          }
        />
      </CenterContent>
    );
  }

  return (
    <CenterContent>
      <Spinner hideDuration={2000} />
    </CenterContent>
  );
};

const shouldSkipProvisioning = (): boolean => {
  const query = qs.parse(window.location.search);
  const skipProvisioning = query.skipProvisioning ?? localStorage.getItem("skipProvisioning");

  if (skipProvisioning === undefined) {
    return false;
  } else if (skipProvisioning) {
    // Query result could, from a type perspective, return a list rather
    // than just a string, so handle that in all the query processing that
    // follows
    const p = typeof skipProvisioning === "object" ? String(skipProvisioning[0]) : skipProvisioning;
    return ["true", "1"].includes(p.toLowerCase());
  }
  return false;
};

const contextFromQueryString = (initUsername?: string, initPassword?: string, initSerial?: string) => {
  // Query result could, from a type perspective, return a list rather than
  // just a string, so handle that in all the query processing that follows
  const query = qs.parse(window.location.search);
  let username = initUsername;
  if (query.username) {
    username = typeof query.username === "object" ? String(query.username[0]) : query.username;
  }
  let password = initPassword;
  if (query.password) {
    password = typeof query.password === "object" ? String(query.password[0]) : query.password;
  }
  let serial = initSerial;
  if (query.serial) {
    serial = typeof query.serial === "object" ? String(query.serial[0]) : query.serial;
  }

  return { username, password, serial };
};

export const Provisioning = ({
  children,
  auth0Config = defaultAuth0Config,
  onLogin,
}: ProvisioningProps): React.JSX.Element => {
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <RenderAuth {...{ children, auth0Config, onLogin }}></RenderAuth>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};
